import { useState, useMemo, useEffect } from "react";
import {
  TextInput,
  Stack,
  Switch,
  Select,
  MultiSelect,
  Paper,
  Group,
  Badge,
  Tooltip,
  Text,
  Box,
  Code,
  Button,
  Divider,
  SegmentedControl,
  Loader,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { RichTextEditor } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import type { Editor } from "@tiptap/core";
import { IconFlask } from "@tabler/icons-react";
import { createClinicalRichTextExtensions } from "../../shared/tiptap/clinicalRichTextExtensions";
import { EditableSection } from "../../pathologist-review/EditableSection";
import { Diagnosis } from "../../../services/admin/diagnosis.service";
import {
  parseVariables,
  replaceVariables,
} from "../../../services/internalGrossCode.service";
import { required, scrollToFirstError } from "../../../utils/formValidation";
import {
  CLINICAL_RICH_TEXT_COLORS,
  CLINICAL_RICH_TEXT_PARSE_OPTIONS,
} from "../../../utils/clinicalRichText";
import {
  useAllActiveStains,
  useDiagnosisLinkedStains,
} from "../../../hooks/admin/useDiagnosis";

export interface DiagnosisFormValues {
  code: string;
  diagnosisCategoryId: string;
  cptId: string;
  icdId: string;
  additionalIcdCodeIds: string[];
  diagnosisDescription: string;
  microscopicDescription: string;
  comments: string;
  isActive: boolean;
  scope: "global" | "personal";
  linkedStainIds: string[];
}

interface DiagnosisFormProps {
  initialValues?: Diagnosis;
  onSubmit: (values: DiagnosisFormValues) => void;
  categories: Array<{ value: string; label: string }>;
  cptCodes: Array<{ value: string; label: string }>;
  icdCodes: Array<{ value: string; label: string }>;
  formId?: string;
}

// Available variables for diagnosis macro templates
const AVAILABLE_VARIABLES = [
  {
    name: "specimen_letter",
    description: "Specimen designation",
    example: "A",
  },
  { name: "source", description: "Tissue source", example: "Colon" },
  { name: "subsite", description: "Specific location", example: "Ascending" },
  {
    name: "location",
    description: "Full specimen location (source + subsite)",
    example: "Colon, Ascending",
  },
  {
    name: "collection_method",
    description: "How collected",
    example: "Polypectomy",
  },
  { name: "size_cm", description: "Size in centimeters", example: "2.5" },
  { name: "block_count", description: "Number of blocks", example: "2" },
  { name: "color", description: "Tissue color", example: "tan-pink" },
  {
    name: "patient_name",
    description: "Patient full name",
    example: "John Smith",
  },
  {
    name: "case_number",
    description: "Case accession number",
    example: "26-G-00042",
  },
  {
    name: "date_collected",
    description: "Date specimen was collected",
    example: "01/15/2026",
  },
];

// Generate sample data for preview
const SAMPLE_DATA: Record<string, string> = AVAILABLE_VARIABLES.reduce(
  (acc, v) => ({ ...acc, [v.name]: v.example }),
  {}
);

const ignorePreviewChange = () => undefined;

export default function DiagnosisForm({
  initialValues,
  onSubmit,
  categories,
  cptCodes,
  icdCodes,
  formId,
}: DiagnosisFormProps) {
  const [showDiagnosisPreview, setShowDiagnosisPreview] = useState(false);
  const [showMicroscopicPreview, setShowMicroscopicPreview] = useState(false);

  const { data: allStains = [], isLoading: stainsLoading } =
    useAllActiveStains();
  const { data: existingLinkedStains = [], isLoading: linkedStainsLoading } =
    useDiagnosisLinkedStains(initialValues?.id);

  const stainOptions = useMemo(
    () =>
      allStains.map((s) => ({
        value: s.id,
        label: `${s.code} — ${s.description} (${s.stainType})`,
      })),
    [allStains]
  );

  const form = useForm<DiagnosisFormValues>({
    initialValues: {
      code: initialValues?.code || "",
      diagnosisCategoryId: initialValues?.diagnosisCategoryId || "",
      cptId: initialValues?.cptId || "",
      icdId: initialValues?.icdId || "",
      additionalIcdCodeIds: (initialValues?.additionalIcdCodes || [])
        .slice()
        .sort((a, b) => a.position - b.position)
        .map((link) => link.icdCodeId),
      diagnosisDescription: initialValues?.diagnosisDescription || "",
      microscopicDescription: initialValues?.microscopicDescription || "",
      comments: initialValues?.comments || "",
      isActive: initialValues?.isActive ?? true,
      scope: initialValues?.scope || "global",
      linkedStainIds: [],
    },
    validate: {
      code: required("Diagnosis code is required"),
      diagnosisDescription: required("Diagnosis description is required"),
    },
  });

  // Populate linked stains once they load (for edit mode)
  useEffect(() => {
    if (existingLinkedStains.length > 0) {
      form.setFieldValue(
        "linkedStainIds",
        existingLinkedStains.map((s) => s.id)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingLinkedStains]);

  // Rich Text Editor for diagnosis description
  const diagnosisEditor = useEditor({
    extensions: createClinicalRichTextExtensions(
      "Enter diagnosis template. Use {{variable_name}} for dynamic fields..."
    ),
    content: form.values.diagnosisDescription || "",
    parseOptions: CLINICAL_RICH_TEXT_PARSE_OPTIONS,
    onUpdate: ({ editor: updateEditor }: { editor: Editor | null }) => {
      if (!updateEditor) return;
      const html = updateEditor.getHTML();
      form.setFieldValue("diagnosisDescription", html);
    },
  }) as Editor | null;

  // Rich Text Editor for microscopic description
  const microscopicEditor = useEditor({
    extensions: createClinicalRichTextExtensions(
      "Enter microscopic description template (optional). Use {{variable_name}} for dynamic fields..."
    ),
    content: form.values.microscopicDescription || "",
    parseOptions: CLINICAL_RICH_TEXT_PARSE_OPTIONS,
    onUpdate: ({ editor: updateEditor }: { editor: Editor | null }) => {
      if (!updateEditor) return;
      const html = updateEditor.getHTML();
      form.setFieldValue("microscopicDescription", html);
    },
  }) as Editor | null;

  // Rich Text Editor for comments
  const commentsEditor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: "Enter additional notes (optional)...",
      }),
    ],
    content: form.values.comments || "",
    onUpdate: ({ editor: updateEditor }: { editor: Editor | null }) => {
      if (!updateEditor) return;
      const html = updateEditor.getHTML();
      form.setFieldValue("comments", html);
    },
  }) as Editor | null;

  // Sync editor content when editing an existing diagnosis
  useEffect(() => {
    if (diagnosisEditor && initialValues) {
      const currentHtml = diagnosisEditor.getHTML();
      if (currentHtml !== initialValues.diagnosisDescription) {
        diagnosisEditor.commands.setContent(
          initialValues.diagnosisDescription || "",
          { parseOptions: CLINICAL_RICH_TEXT_PARSE_OPTIONS }
        );
      }
    }
  }, [initialValues, diagnosisEditor]);

  useEffect(() => {
    if (microscopicEditor && initialValues) {
      const currentHtml = microscopicEditor.getHTML();
      if (currentHtml !== (initialValues.microscopicDescription || "")) {
        microscopicEditor.commands.setContent(
          initialValues.microscopicDescription || "",
          { parseOptions: CLINICAL_RICH_TEXT_PARSE_OPTIONS }
        );
      }
    }
  }, [initialValues, microscopicEditor]);

  useEffect(() => {
    if (commentsEditor && initialValues) {
      const currentHtml = commentsEditor.getHTML();
      if (currentHtml !== (initialValues.comments || "")) {
        commentsEditor.commands.setContent(initialValues.comments || "");
      }
    }
  }, [initialValues, commentsEditor]);

  // Insert variable at cursor position
  const insertVariableInDiagnosis = (variableName: string) => {
    if (diagnosisEditor) {
      diagnosisEditor
        .chain()
        .focus()
        .insertContent(` {{${variableName}}} `)
        .run();
    }
  };

  const insertVariableInMicroscopic = (variableName: string) => {
    if (microscopicEditor) {
      microscopicEditor
        .chain()
        .focus()
        .insertContent(` {{${variableName}}} `)
        .run();
    }
  };

  // Generate previews with sample data
  const diagnosisPreview = useMemo(() => {
    if (!form.values.diagnosisDescription) return "";
    return replaceVariables(form.values.diagnosisDescription, SAMPLE_DATA);
  }, [form.values.diagnosisDescription]);

  const microscopicPreview = useMemo(() => {
    if (!form.values.microscopicDescription) return "";
    return replaceVariables(form.values.microscopicDescription, SAMPLE_DATA);
  }, [form.values.microscopicDescription]);

  // Extract variables from descriptions
  const diagnosisVariables = form.values.diagnosisDescription
    ? parseVariables(form.values.diagnosisDescription)
    : [];
  const microscopicVariables = form.values.microscopicDescription
    ? parseVariables(form.values.microscopicDescription)
    : [];

  return (
    <form
      id={formId}
      noValidate
      onSubmit={form.onSubmit(onSubmit, () => scrollToFirstError(form))}
    >
      <Stack gap="md">
        {/* Code + Category Row */}
        <Group grow align="flex-end">
          <TextInput
            label="Diagnosis Code"
            placeholder="Enter diagnosis code (must be unique)"
            description="Short code used by pathologists for quick diagnosis entry"
            withAsterisk
            {...form.getInputProps("code")}
          />

          <Select
            label="Diagnosis Category"
            placeholder="Select category"
            description=" "
            searchable
            clearable
            data={[...categories].sort((a, b) =>
              a.label.localeCompare(b.label)
            )}
            {...form.getInputProps("diagnosisCategoryId")}
          />
        </Group>

        {/* Scope */}
        <div>
          <Text component="label" fz="sm" fw={500} mb={5}>
            Scope
          </Text>
          <Text size="xs" c="dimmed" mb="xs">
            Global macros are shared with all pathologists. Personal macros are
            only visible to you.
          </Text>
          <SegmentedControl
            data={[
              { value: "global", label: "Global" },
              { value: "personal", label: "Personal" },
            ]}
            {...form.getInputProps("scope")}
          />
        </div>

        {/* Available Variables Helper */}
        <Paper withBorder p="sm">
          <Stack gap="xs">
            <Text size="sm" fw={600}>
              Available Variables (click to insert at cursor)
            </Text>
            <Group gap={6} wrap="wrap">
              {AVAILABLE_VARIABLES.map((variable) => (
                <Tooltip
                  key={variable.name}
                  label={`${variable.description} (e.g., "${variable.example}")`}
                  position="top"
                  withArrow
                >
                  <Badge
                    variant="light"
                    color="indigo"
                    size="md"
                    style={{ cursor: "pointer", fontFamily: "monospace" }}
                    onClick={() => insertVariableInDiagnosis(variable.name)}
                  >
                    {`{{${variable.name}}}`}
                  </Badge>
                </Tooltip>
              ))}
            </Group>
            <Text size="xs" c="dimmed">
              Click a variable to insert it into the currently focused editor.
              Variables are replaced with actual values during diagnosis entry.
            </Text>
          </Stack>
        </Paper>

        {/* Rich Text Diagnosis Description */}
        <div>
          <Text component="label" fz="sm" fw={500} mb={5}>
            Diagnosis Description{" "}
            <Text span c="red">
              *
            </Text>
          </Text>
          <Text size="xs" c="dimmed" mb="xs">
            {
              "Use {{variable_name}} syntax for dynamic fields. Supports bold, italic, underline, and lists."
            }
          </Text>
          <RichTextEditor editor={diagnosisEditor}>
            <RichTextEditor.Toolbar sticky stickyOffset={60}>
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Bold />
                <RichTextEditor.Italic />
                <RichTextEditor.Underline />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.BulletList />
                <RichTextEditor.OrderedList />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Undo />
                <RichTextEditor.Redo />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.ColorPicker
                  colors={CLINICAL_RICH_TEXT_COLORS}
                />
                <RichTextEditor.UnsetColor />
                <RichTextEditor.Highlight />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.ClearFormatting />
              </RichTextEditor.ControlsGroup>
            </RichTextEditor.Toolbar>

            <RichTextEditor.Content style={{ minHeight: "150px" }} />
          </RichTextEditor>
          {form.errors.diagnosisDescription && (
            <Text size="xs" c="red" mt={5}>
              {form.errors.diagnosisDescription}
            </Text>
          )}
        </div>

        {/* Detected Variables & Preview for Diagnosis */}
        {form.values.diagnosisDescription && (
          <Paper withBorder p="sm" bg="blue.0">
            <Stack gap="xs">
              <Group justify="space-between">
                {diagnosisVariables.length > 0 ? (
                  <Text size="xs" fw={600} c="blue">
                    Detected Variables ({diagnosisVariables.length}):
                  </Text>
                ) : (
                  <Text size="xs" fw={600} c="blue">
                    Diagnosis Template
                  </Text>
                )}
                <Button
                  variant="subtle"
                  size="compact-xs"
                  onClick={() => setShowDiagnosisPreview(!showDiagnosisPreview)}
                >
                  {showDiagnosisPreview ? "Hide Preview" : "Show Preview"}
                </Button>
              </Group>
              {diagnosisVariables.length > 0 && (
                <Group gap="xs">
                  {diagnosisVariables.map((variable) => (
                    <Code key={variable}>{`{{${variable}}}`}</Code>
                  ))}
                </Group>
              )}
              {diagnosisVariables.length > 0 && (
                <Text size="xs" c="dimmed">
                  These will be replaced with actual values during diagnosis
                  entry
                </Text>
              )}
            </Stack>
          </Paper>
        )}

        {/* Preview Section for Diagnosis */}
        {showDiagnosisPreview && form.values.diagnosisDescription && (
          <Paper withBorder p="sm" bg="gray.0">
            <Stack gap="xs">
              <Text size="xs" fw={600} c="dimmed">
                Diagnosis Preview (with sample data):
              </Text>
              <EditableSection
                value={diagnosisPreview}
                onChange={ignorePreviewChange}
                isReadOnly
                preserveRichTextLayout
              />
            </Stack>
          </Paper>
        )}

        <Divider label="Microscopic Description" labelPosition="center" />

        {/* Rich Text Microscopic Description */}
        <div>
          <Text component="label" fz="sm" fw={500} mb={5}>
            Microscopic Description
          </Text>
          <Text size="xs" c="dimmed" mb="xs">
            {
              "Auto-populated microscopic description for the report. Supports {{variable_name}} syntax."
            }
          </Text>
          <Box mb="xs">
            <Group gap={6} wrap="wrap">
              {AVAILABLE_VARIABLES.map((variable) => (
                <Tooltip
                  key={variable.name}
                  label={`${variable.description} (e.g., "${variable.example}")`}
                  position="top"
                  withArrow
                >
                  <Badge
                    variant="light"
                    color="grape"
                    size="sm"
                    style={{ cursor: "pointer", fontFamily: "monospace" }}
                    onClick={() => insertVariableInMicroscopic(variable.name)}
                  >
                    {`{{${variable.name}}}`}
                  </Badge>
                </Tooltip>
              ))}
            </Group>
          </Box>
          <RichTextEditor editor={microscopicEditor}>
            <RichTextEditor.Toolbar sticky stickyOffset={60}>
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Bold />
                <RichTextEditor.Italic />
                <RichTextEditor.Underline />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.BulletList />
                <RichTextEditor.OrderedList />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Undo />
                <RichTextEditor.Redo />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.ColorPicker
                  colors={CLINICAL_RICH_TEXT_COLORS}
                />
                <RichTextEditor.UnsetColor />
                <RichTextEditor.Highlight />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.ClearFormatting />
              </RichTextEditor.ControlsGroup>
            </RichTextEditor.Toolbar>

            <RichTextEditor.Content style={{ minHeight: "120px" }} />
          </RichTextEditor>
        </div>

        {/* Detected Variables & Preview for Microscopic */}
        {form.values.microscopicDescription && (
          <Paper withBorder p="sm" bg="grape.0">
            <Stack gap="xs">
              <Group justify="space-between">
                {microscopicVariables.length > 0 ? (
                  <Text size="xs" fw={600} c="grape">
                    Detected Variables ({microscopicVariables.length}):
                  </Text>
                ) : (
                  <Text size="xs" fw={600} c="grape">
                    Microscopic Template
                  </Text>
                )}
                <Button
                  variant="subtle"
                  size="compact-xs"
                  onClick={() =>
                    setShowMicroscopicPreview(!showMicroscopicPreview)
                  }
                >
                  {showMicroscopicPreview ? "Hide Preview" : "Show Preview"}
                </Button>
              </Group>
              {microscopicVariables.length > 0 && (
                <Group gap="xs">
                  {microscopicVariables.map((variable) => (
                    <Code key={variable}>{`{{${variable}}}`}</Code>
                  ))}
                </Group>
              )}
            </Stack>
          </Paper>
        )}

        {/* Preview Section for Microscopic */}
        {showMicroscopicPreview && form.values.microscopicDescription && (
          <Paper withBorder p="sm" bg="gray.0">
            <Stack gap="xs">
              <Text size="xs" fw={600} c="dimmed">
                Microscopic Preview (with sample data):
              </Text>
              <EditableSection
                value={microscopicPreview}
                onChange={ignorePreviewChange}
                isReadOnly
                preserveRichTextLayout
              />
            </Stack>
          </Paper>
        )}

        <Divider label="Billing & Coding" labelPosition="center" />

        {/* CPT + ICD Row */}
        <Group grow align="flex-start">
          <Select
            label="CPT Code"
            placeholder="Select CPT code for billing"
            description="Linked CPT code for automatic billing"
            searchable
            clearable
            data={[...cptCodes].sort((a, b) => a.label.localeCompare(b.label))}
            {...form.getInputProps("cptId")}
          />

          <Select
            label="ICD Code"
            placeholder="Select ICD code"
            description="Primary ICD code for clinical coding"
            searchable
            clearable
            data={[...icdCodes].sort((a, b) => a.label.localeCompare(b.label))}
            {...form.getInputProps("icdId")}
          />
        </Group>

        <MultiSelect
          label="Additional ICD Codes"
          placeholder={
            form.values.additionalIcdCodeIds.length === 0
              ? "Select additional ICD codes"
              : undefined
          }
          description="Extra ICD codes applied with this macro, in claim order after the primary (e.g. B96.81 for the causative organism)"
          searchable
          clearable
          data={[...icdCodes].sort((a, b) => a.label.localeCompare(b.label))}
          {...form.getInputProps("additionalIcdCodeIds")}
        />

        <Divider label="Auto-Linked Stains" labelPosition="center" />

        {/* Auto-Linked Stains */}
        <Stack gap="xs">
          <Group gap="xs" align="center">
            <IconFlask size={16} />
            <Text size="sm" fw={600}>
              Auto-Linked Stains
            </Text>
          </Group>
          <Text size="xs" c="dimmed">
            When this macro is applied to a specimen, the stains selected below
            will be automatically ordered. When the macro is removed, these
            stains are automatically cancelled.
          </Text>
          {stainsLoading || linkedStainsLoading ? (
            <Group gap="xs">
              <Loader size="xs" />
              <Text size="xs" c="dimmed">
                Loading stains...
              </Text>
            </Group>
          ) : (
            <MultiSelect
              placeholder="Search and select stains to auto-order..."
              data={stainOptions}
              searchable
              clearable
              nothingFoundMessage="No stains found"
              {...form.getInputProps("linkedStainIds")}
            />
          )}
          {form.values.linkedStainIds.length > 0 && (
            <Group gap={6} wrap="wrap">
              {form.values.linkedStainIds.map((id) => {
                const stain = allStains.find((s) => s.id === id);
                if (!stain) return null;
                return (
                  <Badge key={id} variant="light" color="teal" size="sm">
                    {stain.code}
                  </Badge>
                );
              })}
            </Group>
          )}
        </Stack>

        <Divider />

        {/* Comments */}
        <div>
          <Text component="label" fz="sm" fw={500} mb={5}>
            Comments
          </Text>
          <RichTextEditor editor={commentsEditor}>
            <RichTextEditor.Toolbar sticky stickyOffset={60}>
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Bold />
                <RichTextEditor.Italic />
                <RichTextEditor.Underline />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.BulletList />
                <RichTextEditor.OrderedList />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.ClearFormatting />
              </RichTextEditor.ControlsGroup>
            </RichTextEditor.Toolbar>

            <RichTextEditor.Content style={{ minHeight: "80px" }} />
          </RichTextEditor>
        </div>

        <Switch
          label="Active"
          description="Inactive macros won't appear in the pathologist's diagnosis entry"
          {...form.getInputProps("isActive", { type: "checkbox" })}
        />
      </Stack>
    </form>
  );
}

export { useForm } from "@mantine/form";
